// StickiesListPanel.js with AI Composition button
import React, { useState, useRef, useEffect } from 'react';
import './StickiesListPanel.css';
import AICompositionModal from '../AICompositionModal';
import StickiesTable from '../MainPanel/StickiesTable';
import { fetchTagDomains } from '../../api/stickiesApi';

const StickiesListPanel = ({ 
  stickies, 
  onExpandSticky, 
  displayModes = {},  // Default to empty object for backwards compatibility
  onToggleDisplayMode,
  selectedStickies = {}, 
  onSelectChange,         
  onSaveLayout = () => console.warn('onSaveLayout not provided'),  // Add this
  isInMainPanel = false,  // ADD THIS LINE
  onRemoveSticky = () => console.warn('onRemoveSticky not provided'), // ADD THIS
  onReorderSticky = () => console.warn('onReorderSticky not provided'), // N
  updateSticky = () => console.warn('updateSticky not provided'),        // NEW
  isRightPanelVisible = false  // Add this
  
}) => {
  //console.log('List Panel props:', { isInMainPanel, isRightPanelVisible });
  // Panel height management st ate
  const [height, setHeight] = useState(30);
  const [isResizing, setIsResizing] = useState(false);
  const startResizeHeight = useRef(0);
  const startResizeY = useRef(0);
  const previousHeight = useRef(150);
  const [freezeButtonText, setFreezeButtonText] = useState('Freeze');
  
  const [editingTitle, setEditingTitle] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [tagDomains, setTagDomains] = useState({});
  const [loading, setLoading] = useState(true);  
  
  // Add state for AI Composition modal
  const [isAICompositionModalOpen, setIsAICompositionModalOpen] = useState(false);

  // In the render section, log what's being shown
  const visibleStickies = stickies.filter(sticky => 
    !displayModes[sticky.id] || 
    displayModes[sticky.id] === 'list' || 
    displayModes[sticky.id] === 'both'
  );
  // console.log('Stickies being shown in list:', visibleStickies.map(s => s.id));

  // Constants for panel height limits
  const MIN_HEIGHT = 30;
  const MAX_HEIGHT = 300;

  // Get an array of pinned sticky IDs
const getSelectedStickyIdsArray = () => {
  const selectedIds = Object.entries(selectedStickies)
    .filter(([_, isSelected]) => isSelected)
    .map(([id]) => id);
    console.log('selectedStickies object:', selectedStickies);
  console.log('selectedIds array:', selectedIds);
  
  return selectedIds;
};  

  useEffect(() => {
  const loadTagDomains = async () => {
    try {
      const domains = await fetchTagDomains();
      setTagDomains(domains);
    } catch (error) {
      console.error('Error loading tag domains:', error);
      setTagDomains({
        "Philosophy": ["thesis", "argument", "analysis"],
        "Programming": ["bug", "feature", "refactor"], 
        "General": ["research", "todo", "important"]
      });
    } finally {
      setLoading(false);
    }
  };

  loadTagDomains();
}, []);
  


  // Handle AI Composition button click
  const handleAICompositionClick = () => {
    setIsAICompositionModalOpen(true);
  };

  // Handler for Send to AI button in modal
  const handleSendToAI = (selectedStickies) => {
    // For now, just log the selected stickies
    console.log('Sending to AI:', selectedStickies);
    
    // Here you would format and send the prompt to your AI service
    // This will be implemented later
    
    // Close the modal after sending
    setIsAICompositionModalOpen(false);
  };

  const handleFreezeAll = () => {
    console.log('Freezing all sticky positions and states');
    const stickyUpdates = stickies.map(sticky => ({
      id: sticky.id,
      x: sticky.x,
      y: sticky.y,
      width: sticky.width,
      height: sticky.height,
      main_x: sticky.main_x,
      main_y: sticky.main_y,
      main_width: sticky.main_width,
      main_height: sticky.main_height,
      display_mode: displayModes[sticky.id] || 'both',
      z_index: sticky.zIndex
    }));

    // Change button text to "Saved!"
    setFreezeButtonText('Saved!');

    // Reset button text after 2 seconds
    setTimeout(() => {
      setFreezeButtonText('Freeze');
    }, 2000);
    
    // Call the parent's save function
    onSaveLayout(stickyUpdates);
  };

  const handleMinimizeAll = () => {
    console.log('Minimizing all unpinned stickies');
    stickies.forEach(sticky => {
      // Only minimize if not pinned
      if (!selectedStickies[sticky.id] && sticky.note_id) {
        //console.log('Minimizing sticky:', sticky.id);
        onToggleDisplayMode(sticky.id, 'list');
      } else {
        console.log('Skipping pinned sticky:', sticky.id);
      }
    });
  };

  // Handle double click to toggle panel height
  const handleHeaderDoubleClick = () => {
    if (height > MIN_HEIGHT) {
      previousHeight.current = height;
      setHeight(MIN_HEIGHT);
    } else {
      setHeight(Math.min(previousHeight.current, MAX_HEIGHT));
    }
  };

  // Panel resize handlers
  const handleResize = (e) => {
    if (!isResizing) return;
    const deltaY = startResizeY.current - e.clientY;
    const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, startResizeHeight.current + deltaY));
    setHeight(newHeight);
  };

  const handleResizeStart = (e) => {
    setIsResizing(true);
    startResizeHeight.current = height;
    startResizeY.current = e.clientY;
    document.body.classList.add('list-panel-resizing');
  };

  const handleResizeEnd = () => {
    if (isResizing) {
      setIsResizing(false);
      document.body.classList.remove('list-panel-resizing');
    }
  };

  // Handle clicking on a sticky in the list
  const handleStickyClick = (sticky) => {
    //console.log('List item clicked:', sticky.id);
    //console.log('Current display modes:', displayModes);
    // If sticky is only in list view, toggle it to both views
    if (displayModes[sticky.id] === 'list') {
      //console.log('Toggling sticky back to both views');
      onToggleDisplayMode(sticky.id, 'both');
    }
    // Bring sticky into focus
    onExpandSticky(sticky.id);
  };

  // Set up resize event listeners
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResize);
      window.addEventListener('mouseup', handleResizeEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleResize);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing]);

  const getStickyIcon = (type) => {
    switch (type) {
      case 'ai': return '🤖';
      case 'checklist': return '☑️';
      default: return '📝';
    }
  };

  // Count pinned stickies for button display
  const pinnedCount = getSelectedStickyIdsArray().length;
  // Handle sticky selection
const handleStickySelect = (stickyId, isSelected) => {
  onSelectChange(stickyId, isSelected);
};

// Handle delete selected stickies
const handleDeleteSelected = async () => {
  if (selectedStickies.size === 0) return;
  
  if (window.confirm(`Delete ${selectedStickies.size} selected stickies? This cannot be undone.`)) {
    for (const stickyId of selectedStickies) {
      try {
        await onRemoveSticky(stickyId);
      } catch (error) {
        console.error(`Failed to delete sticky ${stickyId}:`, error);
      }
    }
    setSelectedStickies(new Set());
  }
};

const handleTitleUpdate = (stickyId, newTitle) => {
  updateSticky(stickyId, { title: newTitle });
};

const handleTagUpdate = (stickyId, tagIndex, newTag) => {
  const tagField = tagIndex === 0 ? 'tag1' : 'tag2';
  updateSticky(stickyId, { [tagField]: newTag });
};

const handleReorderSticky = (stickyId, direction) => {
  const currentIndex = visibleStickies.findIndex(s => s.id === stickyId);
  if (currentIndex === -1) return;

  const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
  if (newIndex < 0 || newIndex >= visibleStickies.length) return;

  const reorderedStickies = [...visibleStickies];
  const [movedSticky] = reorderedStickies.splice(currentIndex, 1);
  reorderedStickies.splice(newIndex, 0, movedSticky);

  const updates = reorderedStickies.map((sticky, index) => ({
    id: sticky.id,
    list_order: index
  }));

  onReorderSticky(updates);
};





  return (
    <div className={`stickies-list-panel ${isInMainPanel ? 'main-panel-list' : ''} ${isRightPanelVisible ? 'right-panel-visible' : ''}`}
      style={{ height: `${height}px` }}>
      {/* Header section - kept unchanged for resize functionality */}
      <div 
        className="list-header list-panel-resize-header"
        onMouseDown={handleResizeStart}
        onDoubleClick={handleHeaderDoubleClick}
      >
        <div className="list-panel-resize-handle" />
        <h3>Sticky Notes ({visibleStickies.length})</h3>
        <button 
          className="minimize-all-button"
          onClick={handleMinimizeAll}
          title="Minimize all unpinned stickies"
        >
          Minimize All
        </button>
        <button
          className="freeze-all-button"
          onClick={handleFreezeAll}
          title="Save current sticky positions and states"
        >
          {freezeButtonText}
        </button>
        {/* Add AI Composition Button */}
        <button 
          className="ai-composition-button"
          onClick={handleAICompositionClick}
          disabled={pinnedCount === 0}
          title="Create AI composition from pinned stickies"
        >
          AI Composition ({pinnedCount})
        </button>
        <button 
            className="delete-selected-button"
            onClick={handleDeleteSelected}
            disabled={getSelectedStickyIdsArray().length === 0}
            title={`Delete ${getSelectedStickyIdsArray().length} selected stickies`}    

          >
            Delete ({getSelectedStickyIdsArray().length})
          </button>
      </div>
      
      {/* List content section - updated with new icon-based layout */}
      <StickiesTable
  stickies={visibleStickies}
  selectedStickies={selectedStickies}
  displayModes={displayModes}
  onStickyClick={handleStickyClick}
  onStickySelect={handleStickySelect}
  onReorderSticky={handleReorderSticky}
  onTitleUpdate={handleTitleUpdate}
  onTagUpdate={handleTagUpdate}
  getStickyIcon={getStickyIcon}
  showContent={false}
/>

      {/* AI Composition Modal */}
      <AICompositionModal
        isOpen={isAICompositionModalOpen}
        onClose={() => setIsAICompositionModalOpen(false)}
        stickies={stickies}
        selectedStickyIds={getSelectedStickyIdsArray()}
        onSendToAI={handleSendToAI}
      />
    </div>
  );
};

export default StickiesListPanel;